import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Text,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { OfertasContext } from '../../context/ofertas-context';
import { useTrackingManager } from '../../tracking/use-tracking-manager';

const FadeContainer = styled.div`
	display: flex;
	align-items: flex-end;
	gap: ${(props) => props.theme.size.static250};

	@media screen and (max-width: 559px) {
		overflow-x: scroll;
		padding-left: ${(props) => props.theme.size.static370};
		padding-right: ${(props) => props.theme.size.static370};

		::-webkit-scrollbar {
			display: none; /* for Chrome, Safari and Opera */
			scrollbar-width: none; /* Firefox */
		}

		-webkit-mask: linear-gradient(
			90deg,
			transparent,
			white 10%,
			white 90%,
			transparent
		);
		mask: linear-gradient(
			90deg,
			transparent,
			white 10%,
			white 90%,
			transparent
		);
	}
	* {
		text-decoration: none;
	}
`;

const MotorizacionTab = styled.span<{ isActive: boolean }>`
	& div {
		text-align: center;
		padding-bottom: ${(props) => props.theme.size.static100};
		border-bottom: ${(props) => props.isActive && '1px solid #000E26'};
	}
`;

export const MotorizacionSelector: React.FC<{
	handleFilteredCarlines: (selectedButton: string) => void;
	selectedMotorizacion: string;
	setSelectedMotorizacion: React.Dispatch<React.SetStateAction<string>>;
}> = ({
	handleFilteredCarlines,
	selectedMotorizacion,
	setSelectedMotorizacion,
}) => {
	const { ofertasService } = React.useContext(OfertasContext);

	const [currentIndex, setCurrentIndex] = useState(0);

	const trackingManager = useTrackingManager();

	const motorizaciones = [
		'Todos',
		'Combustión',
		'Híbridos enchufables',
		'Eléctricos',
	];

	const showSelectorOptions = () => {
		const getOfertasFunctions = [
			ofertasService.getOfertasCombustion(),
			ofertasService.getOfertasHibrido(),
			ofertasService.getOfertasElectrico(),
		];

		return (
			getOfertasFunctions.filter(
				(getOfertasFunction) => getOfertasFunction.length > 0,
			).length > 1
		);
	};

	const handleOnClick = (selector: string, index?: number) => {
		setSelectedMotorizacion(selector);

		trackingManager.trackOfertasFilterSelectClick({
			type: 'single_value',
			name: 'FT',
			values: [selector],
		});

		handleFilteredCarlines(selector);

		if (index) {
			setCurrentIndex(index);
		}
	};

	useEffect(() => {
		const scrollContainer = document.getElementById('scroll-motorizacion-menu');

		const listPosition = motorizaciones.indexOf(selectedMotorizacion);

		let scrollAmount = 20;

		switch (listPosition) {
			case 1:
				scrollAmount = 10;
				break;
			case 2:
				scrollAmount = 100;
				break;
			case motorizaciones.length - 1:
				scrollAmount = 130;
				break;
			default:
				break;
		}

		scrollContainer &&
			scrollContainer.scroll({
				left: scrollAmount,
				behavior: 'smooth',
			});
	}, [currentIndex, selectedMotorizacion]);

	const MotorizacionTabs = (): React.ReactElement => {
		return (
			<FadeContainer id="scroll-motorizacion-menu">
				<CTA
					tag="button"
					emphasis="tertiary"
					onClick={() => handleOnClick(motorizaciones[0], 0)}
				>
					<MotorizacionTab
						isActive={selectedMotorizacion === motorizaciones[0]}
					>
						<Text
							color={
								selectedMotorizacion === motorizaciones[0]
									? TokenTextColor.primary
									: TokenTextColor.secondary
							}
							appearance={TokenTextAppearance.headline100}
							bold
						>
							{motorizaciones[0].toUpperCase()}
						</Text>
					</MotorizacionTab>
				</CTA>

				{ofertasService.getOfertasCombustion().length > 0 && (
					<CTA
						tag="button"
						emphasis="tertiary"
						onClick={() => handleOnClick(motorizaciones[1], 1)}
					>
						<MotorizacionTab
							isActive={selectedMotorizacion === motorizaciones[1]}
						>
							<Text
								color={
									selectedMotorizacion === motorizaciones[1]
										? TokenTextColor.primary
										: TokenTextColor.secondary
								}
								appearance={TokenTextAppearance.headline100}
								bold
							>
								{motorizaciones[1].toUpperCase()}
							</Text>
						</MotorizacionTab>
					</CTA>
				)}

				{ofertasService.getOfertasHibrido().length > 0 && (
					<CTA
						tag="button"
						emphasis="tertiary"
						onClick={() => {
							handleOnClick(motorizaciones[2], 2);
						}}
					>
						<MotorizacionTab
							isActive={selectedMotorizacion === motorizaciones[2]}
						>
							<Text
								color={
									selectedMotorizacion === motorizaciones[2]
										? TokenTextColor.primary
										: TokenTextColor.secondary
								}
								appearance={TokenTextAppearance.headline100}
								bold
							>
								{motorizaciones[2].toUpperCase()}
							</Text>
						</MotorizacionTab>
					</CTA>
				)}

				{ofertasService.getOfertasElectrico().length > 0 && (
					<CTA
						tag="button"
						emphasis="tertiary"
						onClick={() => handleOnClick(motorizaciones[3], 3)}
					>
						<MotorizacionTab
							isActive={selectedMotorizacion === motorizaciones[3]}
						>
							<Text
								color={
									selectedMotorizacion === motorizaciones[3]
										? TokenTextColor.primary
										: TokenTextColor.secondary
								}
								appearance={TokenTextAppearance.headline100}
								bold
							>
								{motorizaciones[3].toUpperCase()}
							</Text>
						</MotorizacionTab>
					</CTA>
				)}
			</FadeContainer>
		);
	};

	return (
		<Container
			data-testid="motorizacion-selector"
			padding={{
				top: {
					[Breakpoints.default]: ContainerPadding.dynamic0130,
					[Breakpoints.b1280]: ContainerPadding.dynamic0150,
				},
				bottom: { [Breakpoints.b960]: ContainerPadding.dynamic0130 },
			}}
		>
			<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
				{showSelectorOptions() && <MotorizacionTabs />}
			</BreakpointWrapper>
			<BreakpointWrapper min={Breakpoints.b960}>
				{showSelectorOptions() && (
					<Container wrap={'never'} gutter={ContainerGutter.static200}>
						<CTA
							tag="button"
							emphasis={
								selectedMotorizacion === motorizaciones[0]
									? 'primary'
									: 'secondary'
							}
							size={'small'}
							onClick={() => handleOnClick(motorizaciones[0])}
						>
							{motorizaciones[0]}
						</CTA>
						{ofertasService.getOfertasCombustion().length > 0 && (
							<CTA
								tag="button"
								emphasis={
									selectedMotorizacion === motorizaciones[1]
										? 'primary'
										: 'secondary'
								}
								size={'small'}
								onClick={() => handleOnClick(motorizaciones[1])}
							>
								{motorizaciones[1]}
							</CTA>
						)}
						{ofertasService.getOfertasHibrido().length > 0 && (
							<CTA
								tag="button"
								emphasis={
									selectedMotorizacion === motorizaciones[2]
										? 'primary'
										: 'secondary'
								}
								size={'small'}
								onClick={() => handleOnClick(motorizaciones[2])}
							>
								{motorizaciones[2]}
							</CTA>
						)}
						{ofertasService.getOfertasElectrico().length > 0 && (
							<CTA
								tag="button"
								emphasis={
									selectedMotorizacion === motorizaciones[3]
										? 'primary'
										: 'secondary'
								}
								size={'small'}
								onClick={() => handleOnClick(motorizaciones[3])}
							>
								{motorizaciones[3]}
							</CTA>
						)}
					</Container>
				)}
			</BreakpointWrapper>
		</Container>
	);
};
